import React, { lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import CitySpecialtySEO from '@/components/seo/CitySpecialtySEO';
import { useCityData } from '@/hooks/useCityData';
import { getCitySpecialtyBySlug } from '@/data/citySpecialties';
import { CityLanguage } from '@/context/languages/languageData';
import { extractLanguageFromPath } from '@/utils/routing/multilingualRouting';
import { generateSpecialtyContent } from '@/utils/seo/specialtyContentGenerator';
import { useCitySpecialtyTranslations } from '@/hooks/useCitySpecialtyTranslations';
import NotFound from './NotFound';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getLocalizedCityPath } from '@/utils/cityLanguage/getLocalizedCityPath';
import RelatedSpecialties from '@/components/city/RelatedSpecialties';
import ErrorBoundary from '@/components/shared/ErrorBoundary';

const CityTherapistSection = lazy(() => import('@/components/city/CityTherapistSection'));

const LocalizedCitySpecialtyLanding = () => {
  const { citySlug, specialtySlug } = useParams<{ citySlug: string; specialtySlug: string }>();
  const urlLanguage = extractLanguageFromPath(window.location.pathname) || 'en';
  const { data: city, isLoading } = useCityData(citySlug);
  
  // Validate URL parameters
  if (!citySlug || !specialtySlug) {
    console.error('Missing required URL parameters:', { citySlug, specialtySlug });
    return <NotFound />;
  }
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  if (!city) {
    console.error('City not found:', citySlug);
    return <NotFound />;
  }
  
  const specialty = getCitySpecialtyBySlug(specialtySlug, urlLanguage as CityLanguage);
  if (!specialty) {
    console.error('Specialty not found:', { specialtySlug, language: urlLanguage });
    return <NotFound />;
  }
  
  const language = (urlLanguage === 'es' ? 'es' : 'en') as CityLanguage;
  
  // Try to load translations from database first
  const { translations: dbTranslations, hasTranslation } = useCitySpecialtyTranslations(
    city,
    specialtySlug,
    language
  );
  
  // Fallback to generated content if no translations available
  const generatedContent = generateSpecialtyContent(city, specialty, language);
  
  // Merge DB translations with generated content
  const content = {
    ...generatedContent,
    ...(hasTranslation && {
      title: dbTranslations.title || generatedContent.title,
      h1: dbTranslations.title || generatedContent.h1,
      heroDescription: dbTranslations.description || generatedContent.heroDescription,
      metaDescription: dbTranslations.description || generatedContent.metaDescription,
    })
  };
  
  const cityPath = getLocalizedCityPath(city.slug, language);

  return (
    <>
      <ErrorBoundary>
        <CitySpecialtySEO
          city={city}
          specialty={specialty}
          language={language}
          title={content.title}
          metaDescription={content.metaDescription}
          faqs={content.faqs}
        />
      </ErrorBoundary>
      
      <ErrorBoundary>
      <div className="container mx-auto px-4 py-8">
        <Link to={cityPath}>
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {language === 'es' ? `Volver a ${city.name}` : `Back to ${city.name}`}
          </Button>
        </Link>

        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{content.h1}</h1>
          <p className="text-xl text-muted-foreground mb-12">{content.heroDescription}</p>
        </div>

        <ErrorBoundary>
          <Suspense fallback={<LoadingSpinner />}>
            <CityTherapistSection city={city} language={language} specialtyFilter={specialty.id} />
          </Suspense>
        </ErrorBoundary>

        <ErrorBoundary>
        <div className="max-w-4xl mx-auto mt-16 space-y-12">
          <section>
            <h2 className="text-3xl font-bold mb-6">{content.whyChooseTitle}</h2>
            <ul className="space-y-3">
              {content.whyChoosePoints.map((point, i) => (
                <li key={i} className="flex items-start">
                  <span className="text-primary mr-2">✓</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">{content.howItWorksTitle}</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {content.howItWorksSteps.map((step, i) => (
                <div key={i} className="bg-card p-6 rounded-lg border">
                  <div className="text-3xl font-bold text-primary mb-2">{i + 1}</div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">{content.faqTitle}</h2>
            <div className="space-y-6">
              {content.faqs.map((faq, i) => (
                <div key={i} className="bg-card p-6 rounded-lg border">
                  <h3 className="text-lg font-semibold mb-2">{faq.question}</h3>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <RelatedSpecialties 
            city={city} 
            currentSpecialty={specialty} 
            language={language} 
          />
        </div>
        </ErrorBoundary>
      </div>
      </ErrorBoundary>
    </>
  );
};

export default LocalizedCitySpecialtyLanding;
